import React from 'react';
import { jsPDF } from 'jspdf';
import '../../assets/fonts/arial-normal';
import {
  Document,
  Packer,
  Paragraph,
  TextRun,
  AlignmentType,
  Table,
  TableRow,
  TableCell,
  WidthType,
  BorderStyle,
} from 'docx';
import { saveAs } from 'file-saver';

export interface ExperienceLetterProps {
  details: {
    employeeName: string;
    email: string;
    designation: string;
    department: string;
    role: string;
    employmentType: string;
    dateOfJoining: string;
    endDate: string;
    companyName: string;
    companyAddress: string;
    signatoryName: string;
    signatoryDesignation: string;
  };
  isDoc?: boolean;
}

function ExperienceLetter({ details, isDoc = false }: ExperienceLetterProps) {
  const {
    employeeName,
    email,
    designation,
    department,
    role,
    employmentType,
    dateOfJoining,
    endDate,
    companyName,
    companyAddress,
    signatoryName,
    signatoryDesignation,
  } = details;

  const startDateText = new Date(dateOfJoining).toDateString();
  const endDateText = new Date(endDate).toDateString();

  const title = 'Ms.';
  const heShe = 'she';
  const hisHer = 'her';
  const himHer = 'her';

  const safeText = (value: any, fallback = '') =>
    value == null ? fallback : String(value);

  const generatePDF = async () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const marginX = 25;
    const contentWidth = pageWidth - marginX * 2;
    const lineSpacing = 7;
    let cursorY = 30;

    doc.setFont('arial', 'bold');
    doc.setFontSize(14);
    cursorY += 20;

    doc.setTextColor(63, 54, 128);
    const titleText = 'TO WHOMSOEVER IT MAY CONCERN';
    const titleWidth = Number(doc.getTextWidth(titleText)) || 0;
    doc.text(
      titleText,
      Number(pageWidth - titleWidth) / 2 || 0,
      Number(cursorY)
    );
    cursorY += lineSpacing * 2;

    doc.setFont('arial', 'normal');
    doc.setFontSize(11);
    doc.setTextColor(0, 0, 0);

    const body = `This is to certify that ${safeText(
      title
    )} ${safeText(employeeName)}, Employee Code ${safeText(
      email
    )}, was employed with ${safeText(companyName)} from ${safeText(
      startDateText
    )} to ${safeText(endDateText)}. At the time of leaving, ${safeText(
      heShe
    )} was working as a ${safeText(designation)} with us.

During ${safeText(hisHer)} tenure with the organization, ${safeText(
      heShe
    )} exhibited sincerity and dedication towards the responsibilities assigned to ${safeText(
      himHer
    )} and maintained good conduct and character throughout.

We sincerely appreciate ${safeText(hisHer)} valuable contributions to the company and wish ${safeText(
      himHer
    )} continued success in all future endeavors.`;

    const bodyLines = doc.splitTextToSize(body, contentWidth);
    doc.text(bodyLines, Number(marginX), Number(cursorY));
    cursorY += bodyLines.length * 5 + lineSpacing * 2;

    const sigX = Number(marginX);

    doc.setFont('arial', 'bold');
    doc.text(`For ${safeText(companyName)}`, sigX, Number(cursorY));
    cursorY += lineSpacing * 1.3;

    doc.setFont('arial', 'normal');
    doc.text(safeText(signatoryName), sigX, Number(cursorY));
    cursorY += lineSpacing;
    doc.text(safeText(signatoryDesignation), sigX, Number(cursorY));
    cursorY += lineSpacing;
    doc.text('Date: _______________', sigX, Number(cursorY));

    cursorY += lineSpacing * 2;
    doc.setFont('arial', 'bold');
    doc.text('Accepted and Agreed by:', Number(marginX), Number(cursorY));
    cursorY += lineSpacing * 1.3;
    doc.setFont('arial', 'normal');
    doc.text(safeText(employeeName), Number(marginX), Number(cursorY));
    cursorY += lineSpacing;
    doc.text('Date: _______________', Number(marginX), Number(cursorY));

    const pageHeight = doc.internal.pageSize.getHeight();
    doc.setFillColor('#8073C0');
    doc.rect(0, Number(pageHeight - 12), Number(pageWidth), 12, 'F');
    doc.setFillColor('#323595');
    doc.rect(0, Number(pageHeight - 18), Number(pageWidth), 6, 'F');

    const filename = `${safeText(employeeName)
      .replace(/\s+/g, '_')
      .replace(/[^a-zA-Z0-9_-]/g, '')}_experience_letter.pdf`;
    doc.save(filename);
  };

  const downloadDocx = async () => {
    const doc = new Document({
      sections: [
        {
          properties: {
            page: {
              margin: { top: 1940, bottom: 720, left: 1440, right: 1440 },
            },
          },
          children: [
            new Paragraph({
              children: [
                new TextRun({
                  text: 'TO WHOMSOEVER IT MAY CONCERN',
                  bold: true,
                  font: 'Arial',
                  size: 26,
                  color: '3F3680',
                }),
              ],
              alignment: AlignmentType.CENTER,
              spacing: { after: 120 },
            }),

            new Paragraph({
              children: [
                new TextRun({
                  text: `This is to certify that ${title} ${employeeName}, Employee Code ${email}, was employed with ${companyName} from ${startDateText} to ${endDateText}. At the time of leaving, ${heShe} was working as a ${designation} with us.\n\nDuring ${hisHer} tenure with the organization, ${heShe} exhibited sincerity and dedication towards the responsibilities assigned to ${himHer} and maintained good conduct and character throughout.\n\nWe sincerely appreciate ${hisHer} valuable contributions to the company and wish ${himHer} continued success in all future endeavors.`,
                  font: 'Arial',
                  size: 20,
                }),
              ],
              spacing: { line: 276, after: 120 },
            }),

            new Paragraph({
              children: [
                new TextRun({
                  text: `For ${companyName}`,
                  bold: true,
                  font: 'Arial',
                  size: 20,
                }),
              ],
            }),
            new Paragraph({
              children: [
                new TextRun({ text: signatoryName, font: 'Arial', size: 20 }),
              ],
            }),
            new Paragraph({
              children: [
                new TextRun({
                  text: signatoryDesignation,
                  font: 'Arial',
                  size: 20,
                }),
              ],
            }),
            new Paragraph({
              children: [
                new TextRun({
                  text: 'Date: _______________',
                  font: 'Arial',
                  size: 20,
                }),
              ],
            }),
            new Paragraph({ children: [new TextRun({ text: '' })] }),

            new Paragraph({
              children: [
                new TextRun({
                  text: 'Accepted and Agreed by:',
                  bold: true,
                  font: 'Arial',
                  size: 20,
                }),
              ],
            }),
            new Paragraph({
              children: [
                new TextRun({ text: employeeName, font: 'Arial', size: 20 }),
              ],
            }),
            new Paragraph({
              children: [
                new TextRun({
                  text: 'Date: _______________',
                  font: 'Arial',
                  size: 20,
                }),
              ],
            }),
            new Paragraph({ children: [new TextRun({ text: '' })] }),

            new Table({
              rows: [
                new TableRow({
                  children: [
                    new TableCell({
                      width: { size: 100, type: WidthType.PERCENTAGE },
                      shading: { fill: '8073C0' },
                      borders: {
                        top: { style: BorderStyle.NONE },
                        bottom: { style: BorderStyle.NONE },
                        left: { style: BorderStyle.NONE },
                        right: { style: BorderStyle.NONE },
                      },
                      children: [
                        new Paragraph({
                          children: [new TextRun({ text: '' })],
                        }),
                      ],
                    }),
                  ],
                }),
                new TableRow({
                  children: [
                    new TableCell({
                      width: { size: 100, type: WidthType.PERCENTAGE },
                      shading: { fill: '323595' },
                      borders: {
                        top: { style: BorderStyle.NONE },
                        bottom: { style: BorderStyle.NONE },
                        left: { style: BorderStyle.NONE },
                        right: { style: BorderStyle.NONE },
                      },
                      children: [
                        new Paragraph({
                          children: [new TextRun({ text: '' })],
                        }),
                      ],
                    }),
                  ],
                }),
              ],
            }),
          ],
        },
      ],
    });

    const blob = await Packer.toBlob(doc);
    saveAs(
      blob,
      `${employeeName.replace(/\s+/g, '_')}_experience_letter.docx`
    );
  };

  return (
    <div>
      <button
        onClick={isDoc ? downloadDocx : generatePDF}
        style={{
          backgroundColor: '#6633ff',
          color: '#fff',
          padding: '10px 20px',
          fontSize: '14px',
          fontWeight: 600,
          border: 'none',
          borderRadius: '8px',
          boxShadow: '0 4px 10px rgba(102, 51, 255, 0.2)',
          cursor: 'pointer',
        }}
      >
        Download Experience Letter
      </button>
    </div>
  );
}

export default ExperienceLetter;
